import React, { FC, MouseEvent } from 'react';
import {
    Box,
    Chip,
    FormControl,
    FormControlLabel,
    MenuItem,
    OutlinedInput,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { getTranslatedLabelsSelect } from 'helpers/filters';
import { PersonalizeType } from '../../types';
import { useAppSelector } from 'app/hooks';

const Personalize: FC<PersonalizeType> = ({ structures, toneOfVoice, anonymized, onChange }) => {
    const { t } = useTranslation();
    const { additionalSettings } = useAppSelector((state) => state.global);

    const structuresOptions = getTranslatedLabelsSelect(
        additionalSettings.STRUCTURES,
        'structures',
    );
    const toneOfVoiceOptions = getTranslatedLabelsSelect(
        additionalSettings.TONES_OF_VOICE,
        'tone_of_voice',
    );

    const handleAnonymizedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        onChange('anonymized', value);
    };

    const handleToneChange = (event: SelectChangeEvent<string>) => {
        const { value } = event.target;
        onChange('tone_of_voice', value);
    };

    const handleStructuresChange = (event: SelectChangeEvent<string[]>) => {
        const { value } = event.target;
        const val = Array.isArray(value) ? value.map((it) => +it) : +value;
        onChange('structures', val);
    };

    const handleChipDelete = (value: string) => {
        const val = structures.filter((it) => it !== +value);
        onChange('structures', val);
    };

    return (
        <>
            <FormControl className="filter-container">
                <Box className="filter-container-header" style={{ paddingBottom: '6px' }}>
                    {t('web_app.additional_settings.anonymized_title')}
                </Box>
                <RadioGroup
                    className="radio-group"
                    name="radio-buttons-group"
                    onChange={handleAnonymizedChange}
                    value={anonymized ?? 'no'}
                >
                    <FormControlLabel
                        control={<Radio />}
                        label={t('web_app.additional_settings.anonymized.no')}
                        value={'no'}
                    />
                    <FormControlLabel
                        control={<Radio />}
                        label={t('web_app.additional_settings.anonymized.yes')}
                        value={'yes'}
                    />
                </RadioGroup>
                <Box className="filter-container-body-description" style={{ marginTop: '0' }}>
                    {t('web_app.additional_settings.anonymized_tooltip')}
                </Box>
            </FormControl>

            <Box className="select-inputs">
                <FormControl fullWidth className="filter-container">
                    <Box className="filter-container-header" style={{ paddingBottom: '6px' }}>
                        {t('web_app.additional_settings.tone_of_voice_title')}
                    </Box>
                    <Select
                        inputProps={{ 'aria-label': 'tone of voice' }}
                        value={toneOfVoice ? toneOfVoice.toString(10) : ''}
                        onChange={handleToneChange}
                    >
                        {toneOfVoiceOptions.map((tone) => (
                            <MenuItem key={tone.value} value={tone.id} id={tone.id}>
                                {tone.label}
                            </MenuItem>
                        ))}
                    </Select>
                    <Box className="filter-container-body-description">
                        {t('web_app.additional_settings.tone_of_voice_tooltip')}
                    </Box>
                </FormControl>
                <FormControl fullWidth className="filter-container">
                    <Box className="filter-container-header" style={{ paddingBottom: '6px' }}>
                        {t('web_app.additional_settings.structures_title')}
                    </Box>
                    <Select
                        multiple
                        className="structures"
                        onChange={handleStructuresChange}
                        input={<OutlinedInput />}
                        inputProps={{ 'aria-label': 'structures' }}
                        value={structures ? structures.map((it) => it.toString(10)) : []}
                        renderValue={(selected: string[]) => (
                            <Box className="structure-chips">
                                {selected.map((value) => {
                                    return (
                                        <Chip
                                            onMouseDown={(event: MouseEvent) => {
                                                event.stopPropagation();
                                            }}
                                            className="structure-chip"
                                            key={`select-structures-${value}`}
                                            label={
                                                structuresOptions?.find((item) => item.id == +value)
                                                    ?.label
                                            }
                                            onDelete={() => handleChipDelete(value)}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                    >
                        {structuresOptions
                            .filter((structure) => !structures?.includes(structure.id))
                            .map((structure) => (
                                <MenuItem key={structure.value} value={structure.id.toString(10)}>
                                    {structure.label}
                                </MenuItem>
                            ))}
                    </Select>
                    <Box className="filter-container-body-description">
                        {t('web_app.additional_settings.structures_tooltip')}
                    </Box>
                </FormControl>
            </Box>
        </>
    );
};

export default Personalize;
